import { useState, useEffect } from 'react';
import NannyCard from '../components/NannyCard';
import { supabase } from '../supabase';

const NANNY_FILTERS = ['All','Special needs','Overnight','After-school','Urgent available','Live-in','Weekends'];
const JOB_FILTERS = ['All jobs','Full-time','Part-time','Special needs','Overnight','Urgent'];

export default function Nannies({ go, onBook }) {
  const [tab, setTab] = useState('nannies');
  const [filter, setFilter] = useState('All');
  const [jobFilter, setJobFilter] = useState('All jobs');
  const [nannies, setNannies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const [{ data: n }, { data: j }] = await Promise.all([
        supabase.from('nannies').select('*').order('created_at'),
        supabase.from('jobs').select('*').order('created_at'),
      ]);
      setNannies(n || []);
      setJobs(j || []);
      setLoading(false);
    }
    fetchData();
  }, []);

  const filtered = filter === 'All' ? nannies : nannies.filter(n =>
    filter === 'Urgent available' ? n.urgent :
    filter === 'Weekends' ? (n.days[5] || n.days[6]) :
    filter === 'Live-in' ? n.specialties?.some(s => s.toLowerCase().includes('live')) :
    n.specialties?.some(s => s.toLowerCase().includes(filter.toLowerCase().replace('-', '')))
  );

  const filteredJobs = jobFilter === 'All jobs' ? jobs : jobs.filter(j =>
    jobFilter === 'Urgent' ? j.type === 'Urgent' :
    j.type === jobFilter || j.title?.toLowerCase().includes(jobFilter.toLowerCase())
  );

  if(loading) return <div style={{ padding:'80px', textAlign:'center', color:'var(--text-light)' }}>Loading caregivers...</div>;

  return (
    <div style={{ padding:'48px 1.5rem', minHeight:'80vh' }}>
      <style>{`
        .nannies-header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:32px; flex-wrap:wrap; gap:16px; }
        .nanny-cards-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:1.25rem; margin-bottom:32px; }
        .jobs-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.25rem; margin-bottom:32px; }
        .jobs-banner { display:flex; justify-content:space-between; align-items:center; gap:16px; flex-wrap:wrap; }
        @media (max-width: 600px) {
          .nanny-cards-grid { grid-template-columns:1fr; }
          .jobs-grid { grid-template-columns:1fr; }
          .jobs-banner { flex-direction:column; text-align:center; }
          .jobs-banner button { width:100%; }
        }
      `}</style>

      <div className="container">
        <div className="nannies-header">
          <div>
            <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(28px,4vw,48px)', color:'var(--teal-deeper)', marginBottom:8 }}>Available nannies</h2>
            <p style={{ fontSize:15, color:'var(--text-mid)', maxWidth:560 }}>Every caregiver below has passed our full verification process.</p>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:28, color:'var(--teal)' }}>{nannies.filter(n=>n.available).length} available</div>
            <div style={{ fontSize:12, color:'var(--text-light)' }}>Updated in real time</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
          {[['nannies','👩 Find a nanny'],['jobs','💼 Available jobs']].map(([id,label]) => (
            <button key={id} onClick={() => { setTab(id); setFilter('All'); setJobFilter('All jobs'); }} style={{ padding:'10px 20px', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', border:'1.5px solid var(--border)', background:tab===id?'var(--teal)':'white', color:tab===id?'white':'var(--text-mid)', fontFamily:"'DM Sans',sans-serif", transition:'all .2s' }}>{label}</button>
          ))}
        </div>

        {/* NANNIES TAB */}
        {tab==='nannies' && <>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24 }}>
            {NANNY_FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding:'7px 14px', borderRadius:20, fontSize:13, fontWeight:500, border:`1px solid ${filter===f?'var(--teal-mid)':'var(--border)'}`, background:filter===f?'var(--teal-light)':'white', color:filter===f?'var(--teal-dark)':'var(--text-mid)', cursor:'pointer', transition:'all .2s', fontFamily:"'DM Sans',sans-serif" }}>{f}</button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1.5rem', background:'var(--warm)', borderRadius:16 }}>
              <div style={{ fontSize:48, marginBottom:16 }}>👩</div>
              <h3 style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, color:'var(--teal-deeper)', marginBottom:12 }}>Caregivers coming soon</h3>
              <p style={{ fontSize:15, color:'var(--text-mid)', marginBottom:24 }}>Contact us directly and we'll find the perfect match for your family.</p>
              <button className="btn-primary" onClick={() => go('consult')}>Contact us →</button>
            </div>
          ) : (
            <div className="nanny-cards-grid">
              {filtered.map(n => <NannyCard key={n.id} n={n} onBook={onBook}/>)}
            </div>
          )}

          <div style={{ textAlign:'center', padding:'2rem 1.5rem', background:'var(--warm)', borderRadius:16 }}>
            <p style={{ fontSize:15, color:'var(--text-mid)', marginBottom:16 }}>Don't see the right fit? We have more caregivers in our network.</p>
            <button className="btn-primary" onClick={() => go('consult')}>Request a custom match →</button>
          </div>
        </>}

        {/* JOBS TAB */}
        {tab==='jobs' && <>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24 }}>
            {JOB_FILTERS.map(f => (
              <button key={f} onClick={() => setJobFilter(f)} style={{ padding:'7px 14px', borderRadius:20, fontSize:13, fontWeight:500, border:`1px solid ${jobFilter===f?'var(--teal-mid)':'var(--border)'}`, background:jobFilter===f?'var(--teal-light)':'white', color:jobFilter===f?'var(--teal-dark)':'var(--text-mid)', cursor:'pointer', transition:'all .2s', fontFamily:"'DM Sans',sans-serif" }}>{f}</button>
            ))}
          </div>

          {filteredJobs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1.5rem', background:'var(--warm)', borderRadius:16, marginBottom:24 }}>
              <div style={{ fontSize:48, marginBottom:16 }}>💼</div>
              <h3 style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, color:'var(--teal-deeper)', marginBottom:12 }}>No jobs posted yet</h3>
              <p style={{ fontSize:15, color:'var(--text-mid)', marginBottom:24 }}>Check back soon or contact us to post a position.</p>
              <button className="btn-primary" onClick={() => go('consult')}>Post a job →</button>
            </div>
          ) : (
            <div className="jobs-grid">
              {filteredJobs.map(j => (
                <div key={j.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:'1.5rem', display:'flex', flexDirection:'column', gap:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700 }}>{j.title}</div>
                      <div style={{ fontSize:12, color:'var(--text-light)', marginTop:2 }}>{j.family}</div>
                    </div>
                    <span style={{ background:j.type==='Urgent'?'var(--coral-light)':'var(--teal-light)', color:j.type==='Urgent'?'var(--coral)':'var(--teal-dark)', borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>{j.type}</span>
                  </div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap', fontSize:12, color:'var(--text-mid)' }}>
                    <span>⏰ {j.schedule}</span><span>💰 {j.rate}</span><span>👦 {j.ages}</span>
                  </div>
                  <p style={{ fontSize:13, color:'var(--text-mid)', lineHeight:1.6 }}>{j.description}</p>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {j.requirements?.map(r => <span key={r} style={{ background:'#f4f4f4', color:'#555', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600 }}>{r}</span>)}
                  </div>
                  <button className="btn-primary" style={{ padding:10, fontSize:13, borderRadius:8 }} onClick={() => go('consult')}>Apply now →</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ background:'var(--teal-deeper)', borderRadius:16, padding:'1.5rem' }}>
            <div className="jobs-banner">
              <div>
                <h4 style={{ fontSize:17, fontWeight:700, color:'white', marginBottom:4 }}>Are you a caregiver looking for work?</h4>
                <p style={{ fontSize:13, color:'var(--teal-mid)' }}>Join the Tinytrust network — get matched with families.</p>
              </div>
              <button className="btn-coral" style={{ padding:'12px 24px', fontSize:14 }} onClick={() => go('consult')}>Apply to join →</button>
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}